import { createContext } from 'react';

import { SelectOption } from '@vchasno/ui-kit';

import { t } from 'ttag';

import {
  BodyTaskUploadDocument,
  DocumentActionType,
  FileDocumentInfoSerializer,
  HrsDocumentCategory,
  TaskDocumentAccessSettings,
  TaskRetrieveSerializer,
  TaskRouteDocumentActionUpdateSerializer,
  TaskRouteDocumentUpdateSerializer,
} from 'api/api';
import { captureException } from 'lib/sentry';

import { buildFullName } from 'utils/name.util';

import {
  UniversalDocument,
  UniversalDocumentProcess,
  UniversalDocumentSettingsSection,
  UniversalDocumentState,
} from './universalDocument.types';

interface UniversalDocumentModalContextType {
  close: VoidFunction;
  visible: boolean;
}

export const UniversalDocumentModalContext =
  createContext<UniversalDocumentModalContextType>({
    visible: false,
    close: () => null,
  });

const HRS_DOCUMENT_CATEGORY_LOCALIZATION: Map<HrsDocumentCategory, string> =
  new Map([
    [HrsDocumentCategory.Act, t`Акт наданих послуг`],
    [HrsDocumentCategory.Contract, t`Договір`],
    [HrsDocumentCategory.Application, t`Заява`],
    [HrsDocumentCategory.Appendix, t`Додаток`],
    [HrsDocumentCategory.Another, t`Інший`],
    [HrsDocumentCategory.Order, t`Наказ`],
    [HrsDocumentCategory.Report, t`Звіт`],
    [HrsDocumentCategory.Letter, t`Лист`],
  ]);

export function localizeHrsDocumentCategory(
  category: HrsDocumentCategory,
): string {
  if (!HRS_DOCUMENT_CATEGORY_LOCALIZATION.has(category)) {
    captureException(new Error(`No localization found for category`), {
      category,
    });

    return category;
  }

  return HRS_DOCUMENT_CATEGORY_LOCALIZATION.get(category)!;
}

export const HRS_DOCUMENT_CATEGORY_OPTIONS: SelectOption[] = Object.values(
  HrsDocumentCategory,
).map((el) => ({
  label: localizeHrsDocumentCategory(el),
  value: el,
}));

const DOCUMENT_SETTINGS_SECTION_LABEL: Map<
  UniversalDocumentSettingsSection,
  string
> = new Map([
  [UniversalDocumentSettingsSection.FILE, t`Інформація про документ`],
  [UniversalDocumentSettingsSection.SIGNERS, t`Підписанти`],
  [UniversalDocumentSettingsSection.ACCESS_SETTINGS, t`Доступ`],
]);

export function localizeDocumentSettingsSection(
  section: UniversalDocumentSettingsSection,
): string {
  if (!DOCUMENT_SETTINGS_SECTION_LABEL.has(section)) {
    captureException(new Error(`No localization found for section`), {
      section,
    });

    return section;
  }

  return DOCUMENT_SETTINGS_SECTION_LABEL.get(section)!;
}

export const isHrsDocumentCategory = (
  value: unknown,
): value is HrsDocumentCategory => {
  return (
    typeof value === 'string' &&
    Object.values(HrsDocumentCategory).includes(value as HrsDocumentCategory)
  );
};

export function findActiveDocument(state: UniversalDocumentState) {
  if (!state.activeDocumentId) {
    if (!state.isUniqueDocumentSettings) {
      const firstDocument = Object.values(state.documents)[0];

      if (firstDocument) return firstDocument;
    }

    return null;
  }

  const document = state.documents[state.activeDocumentId];

  return document ?? null;
}

export function buildStateFromRetrieveTask(
  state: UniversalDocumentState,
  retrieveTask: TaskRetrieveSerializer,
): UniversalDocumentState {
  const nextState = { ...state };

  nextState.initiator = {
    label: buildFullName(
      retrieveTask.initiator.first_name,
      retrieveTask.initiator.last_name,
      retrieveTask.initiator.middle_name,
    ),
    value: retrieveTask.initiator.id,
  };

  nextState.company = {
    label: retrieveTask.company.name,
    value: retrieveTask.company.id,
  };

  nextState.documents = retrieveTask.route.reduce<
    Record<string, UniversalDocument>
  >((acc, route) => {
    if (!route?.file_document) return acc;

    if (nextState.documents[route.file_document.id]) {
      return {
        ...acc,
        [route.file_document.id]: nextState.documents[route.file_document.id],
      };
    }

    acc[route.file_document.id] = {
      id: route.file_document.id,
      routeId: route.id,
      file: {
        name: route.file_document.name,
        number: route.document_info?.document_number || null,
        category: route.document_info?.category || null,
      },
      signers: route.actions
        .filter((el) => el.executor)
        .map((el) => {
          const executor = el.executor!;

          return {
            fullName: buildFullName(
              executor.first_name,
              executor.last_name,
              executor.middle_name,
            ),
            id: executor.id,
            position: executor.position,
            deletable: executor.id !== retrieveTask.initiator.id,
            actionType: el.action_type ?? DocumentActionType.Sign,
          };
        }),
      isParallel: route.is_parallel_action ?? false,
      accessSettings:
        route.access_settings ?? TaskDocumentAccessSettings.Extended,
    };

    return acc;
  }, {});

  if (state.process === UniversalDocumentProcess.EDIT) {
    const hasGrouping = retrieveTask.route.some((el) => el.grouping_id);

    nextState.isUniqueDocumentSettings = !hasGrouping;

    if (!hasGrouping) {
      nextState.allowedDocumentSections = [
        UniversalDocumentSettingsSection.FILE,
        UniversalDocumentSettingsSection.SIGNERS,
        UniversalDocumentSettingsSection.ACCESS_SETTINGS,
      ];
    }
  }

  const firstRoute = retrieveTask.route.find((el) => el.file_document?.id);

  if (
    !nextState.activeDocumentId ||
    !nextState.documents[nextState.activeDocumentId]
  ) {
    nextState.activeDocumentId = firstRoute?.file_document?.id || null;
  }

  return nextState;
}

export const serializeTaskRouteDocument = (
  documentId: string,
  document: Nullable<UniversalDocument>,
  actions: TaskRouteDocumentActionUpdateSerializer[],
): TaskRouteDocumentUpdateSerializer => {
  return {
    file_document_id: documentId,
    document_info: serializeDocumentInfo(document),
    access_settings:
      document?.accessSettings ?? TaskDocumentAccessSettings.Extended,
    is_parallel_action: document?.isParallel ?? false,
    actions,
  };
};

export const serializeDocumentInfo = (
  document: Nullable<UniversalDocument>,
): Nullable<FileDocumentInfoSerializer> => {
  if (!document?.file) return null;

  return {
    name: document.file.name,
    document_number: document.file.number,
    category: document.file.category,
  };
};

export const isBodyTaskUploadDocument = (
  formData: unknown,
): formData is BodyTaskUploadDocument => {
  return (
    formData instanceof FormData &&
    formData.has('file') &&
    formData.get('file') instanceof File
  );
};

const ALLOWED_FILE_EXTENSIONS = new Set([
  'png',
  'jpeg',
  'jpg',
  'svg',
  'pdf',
  'xls',
  'xlsx',
  'csv',
  'doc',
  'docx',
  'txt',
  'xml',
]);

export const SUPPORTED_FILE_EXTENSIONS_LIST = Array.from(
  ALLOWED_FILE_EXTENSIONS,
)
  .map((el) => `.${el}`)
  .join(',');

export const isNotSupportedExtension = (extension?: string) => {
  if (!extension) return false;

  return !ALLOWED_FILE_EXTENSIONS.has(extension.toLowerCase());
};

const DOCUMENT_SEARCH_TERMS: Record<HrsDocumentCategory, string[]> = {
  [HrsDocumentCategory.Act]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Act).toLowerCase(),
    'акт',
  ],
  [HrsDocumentCategory.Contract]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Contract).toLowerCase(),
  ],
  [HrsDocumentCategory.Application]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Application).toLowerCase(),
  ],
  [HrsDocumentCategory.Appendix]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Appendix).toLowerCase(),
  ],
  [HrsDocumentCategory.Another]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Another).toLowerCase(),
  ],
  [HrsDocumentCategory.Order]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Order).toLowerCase(),
    'розпорядження',
  ],
  [HrsDocumentCategory.Report]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Report).toLowerCase(),
  ],
  [HrsDocumentCategory.Letter]: [
    localizeHrsDocumentCategory(HrsDocumentCategory.Letter).toLowerCase(),
  ],
};

const DOCUMENT_CATEGORIES_LOOKUP: Map<string, HrsDocumentCategory> = new Map();

Object.entries(DOCUMENT_SEARCH_TERMS).forEach(([category, terms]) => {
  terms.forEach((term) => {
    if (term) {
      DOCUMENT_CATEGORIES_LOOKUP.set(term, category as HrsDocumentCategory);
    }
  });
});

export function buildDocumentCategoryByName(
  name?: Nullable<string>,
): Nullable<HrsDocumentCategory> {
  if (!name) {
    return null;
  }

  const fileName = name.toLowerCase();

  for (const [term, category] of DOCUMENT_CATEGORIES_LOOKUP.entries()) {
    if (fileName.includes(term)) {
      return category;
    }
  }

  return null;
}
